"use client";

import { Input } from "@/components/ui/input";
import { Fold } from "./fold";
import type { LanguageCode } from "@/constants/languages";
import type { AttributeLabels, AttributeRow } from "./use-category-drafts";

interface AttributeLabelsTableProps {
  rows: AttributeRow[];
  languages: LanguageCode[];
  labels: AttributeLabels;
  readOnly: boolean;
  onChange: (labels: AttributeLabels) => void;
}

/**
 * Labels for the catalogue's other languages. The first language comes from the
 * name typed in the attribute list, so it is not asked for twice.
 */
export function AttributeLabelsTable({
  rows,
  languages,
  labels,
  readOnly,
  onChange,
}: AttributeLabelsTableProps) {
  const named = rows.filter((r) => r.label.trim() && r.key);
  const others = languages.slice(1);

  const total = named.length * others.length;
  const filled = named.reduce(
    (sum, row) =>
      sum +
      others.filter((lang) => (labels[row.key]?.[lang] ?? "").trim()).length,
    0,
  );

  const state = !total
    ? "Nothing to translate yet"
    : filled === total
      ? "All filled in"
      : `${filled} of ${total} filled in`;

  const set = (key: string, lang: string, value: string) =>
    onChange({ ...labels, [key]: { ...(labels[key] ?? {}), [lang]: value } });

  return (
    <Fold title="Required labels in every language" state={state}>
      {!named.length ? (
        <p className="text-xs text-muted-foreground">
          Name an attribute above and it will appear here.
        </p>
      ) : !others.length ? (
        <p className="text-xs text-muted-foreground">
          This catalogue only uses one language, so there is nothing to translate.
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            What shoppers see each attribute called. Every selected catalogue language is required.
          </p>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border-b bg-muted/40 px-2.5 py-2 text-left text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                    Attribute
                  </th>
                  {others.map((lang) => (
                    <th
                      key={lang}
                      className="border-b bg-muted/40 px-2.5 py-2 text-left text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground"
                    >
                      {lang}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {named.map((row) => (
                  <tr key={row.key} className="border-b last:border-b-0">
                    <td className="px-2.5 py-1.5 text-[13px]">{row.label}</td>
                    {others.map((lang) => (
                      <td key={lang} className="px-2 py-1">
                        <Input
                          className="h-8 text-xs"
                          placeholder={`${row.label} (required)`}
                          disabled={readOnly}
                          value={labels[row.key]?.[lang] ?? ""}
                          onChange={(e) => set(row.key, lang, e.target.value)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Fold>
  );
}
